<script>
  import { Link } from '@inertiajs/svelte'
  import EditIcon from '../EditIcon/index.svelte'
  import StatusBadge from '../StatusBadge.svelte'

  // A single item row, shared by ItemsList and the dashboard's recent/favorite
  // lists. Self-sufficient: links derive from item.property_id, so it works
  // across properties. Move/delete are delegated to the parent via callbacks
  // (the parent owns the modal + confirmation), keeping this row stateless.
  let {
    item,
    onMove = null,    // (item) => void — show move button when provided
    onDelete = null,  // (item) => void — show delete button when provided
    // When true (e.g. the narrow dashboard columns) the actions drop to their
    // own row beneath the item info and stay visible, instead of crowding to
    // the right and hover-hiding. ItemsList (full width) leaves this off.
    stackActions = false
  } = $props()

  const propertyId = $derived(item.property_id)
  // Containers (items with a container_type) have their own route.
  const itemHref = $derived(
    item.container_type
      ? `/containers/${item.id}`
      : `/properties/${propertyId}/items/${item.id}`
  )

  function formatCategory(category) {
    if (!category) return null
    return category.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }
</script>

<li class="group px-3 py-2.5 rounded-xl hover:bg-gray-50 {stackActions ? 'flex flex-col gap-2' : 'flex items-center gap-3.5'}">
  <!-- Top content: thumb + info + location. In stacked mode this is its own
       full-width row; otherwise it grows beside the actions. -->
  <div class="flex items-center gap-3.5 min-w-0 {stackActions ? 'w-full' : 'flex-1'}">
  <!-- Thumbnail (clickable) -->
  <Link href={itemHref} class="flex-shrink-0" aria-label={`View ${item.name}`}>
    {#if item.image_url}
      <img src={item.image_url} alt={item.name} class="h-13 w-13 object-cover rounded-lg bg-gray-100" />
    {:else}
      <div class="h-13 w-13 rounded-lg bg-brand-50 text-brand-700 grid place-items-center text-lg font-bold">
        {(item.name || '?').charAt(0).toUpperCase()}
      </div>
    {/if}
  </Link>

  <div class="flex-1 min-w-0">
    <h3 class="text-sm font-semibold truncate">
      <Link href={itemHref} class="text-gray-900 hover:text-brand-700">{item.name}</Link>
    </h3>
    {#if item.description}
      <p class="text-xs text-gray-500 truncate">{item.description}</p>
    {/if}
    <div class="flex flex-wrap items-center gap-1.5 mt-1">
      {#if item.category}
        <span class="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
          {formatCategory(item.category)}
        </span>
      {/if}
      <StatusBadge value={item.status} />
      <StatusBadge value={item.condition} />
      {#if item.low_stock}
        <span class="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" title={`${item.used_quantity ?? ''} used of ${item.initial_quantity ?? ''}`}>
          <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>
          Low stock
        </span>
      {/if}
      {#if item.is_valuable}
        <svg class="h-3.5 w-3.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Valuable"><title>Valuable</title><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
      {/if}
      {#if item.is_favorite}
        <svg class="h-3.5 w-3.5 text-red-500" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Favorite"><title>Favorite</title><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
      {/if}
    </div>
  </div>

  <!-- Location -->
  <div class="hidden md:flex items-center gap-1.5 flex-shrink-0 text-xs text-gray-500">
    <svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
    {#if item.container}
      {item.container.label}
    {:else if item.space}
      {item.space.name}
    {:else}
      Unassigned
    {/if}
  </div>
  </div>

  <!-- Actions (only rendered when the parent provides handlers). Stacked mode:
       full-width row beneath the content, always visible. Default: right-
       aligned, revealed on hover/focus. -->
  {#if onMove || onDelete}
    <div class={stackActions
      ? 'flex items-center gap-1.5 w-full pl-[4.125rem]'
      : 'flex items-center gap-1 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity'}>
      {#if onMove}
        <button
          onclick={() => onMove(item)}
          class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-brand-700 hover:border-brand-600 transition-colors"
          title={`Move ${item.name}`}
          aria-label={`Move ${item.name}`}
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17h9a4 4 0 004-4V7"/><path d="M14 7l3-3 3 3"/></svg>
        </button>
      {/if}
      <EditIcon
        href={`${itemHref}/edit`}
        label={`Edit ${item.name}`}
        className="p-2 rounded-lg border border-gray-200 bg-white hover:text-brand-700 hover:border-brand-600"
      />
      {#if onDelete}
        <button
          onclick={() => onDelete(item)}
          class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
          title="Delete item"
          aria-label={`Delete ${item.name}`}
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      {/if}
    </div>
  {/if}
</li>
